import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useExercisePlanStore } from '@/stores/exercisePlanStore'
import { useAuthStore } from '@/stores/authStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { fetchExercisePlans } from '@/services/exercisePlanAPI'
import { Plus, Search, Save, Users, Target, Dumbbell, RefreshCw } from 'lucide-react'

// Import components
import PlanLibrary from './components/PlanLibrary'
import PlanBuilder from './components/PlanBuilder'
import ExerciseLibraryModal from './components/ExerciseLibraryModal'
import CreatePlanModal from './components/CreatePlanModal'
import AssignClientModal from './components/AssignClientModal'

const ExercisePlanBuilder = () => {
  const location = useLocation()
  const { clientId: pathClientId, sessionId: pathSessionId } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()

  const sessionIdFromParams =
    searchParams.get('sessionId') ||
    searchParams.get('programId') ||
    pathSessionId ||
    location.state?.sessionId ||
    location.state?.programId ||
    ''
  const clientIdFromParams =
    searchParams.get('clientId') ||
    pathClientId ||
    location.state?.clientId ||
    ''

  const resolvedSessionId = sessionIdFromParams ? String(sessionIdFromParams) : ''
  const resolvedClientId = clientIdFromParams
    ? String(clientIdFromParams)
    : user?.id
      ? String(user.id)
      : ''
  
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showExerciseLibrary, setShowExerciseLibrary] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({
    difficulty: 'all',
    assignedStatus: 'all'
  })

  const {
    plans,
    selectedPlan,
    setSelectedPlan,
    createPlan,
    updatePlan,
    duplicatePlan,
    deletePlan,
    archivePlan,
    loadPlansFromAPI,
    loadExerciseLibrary,
    exerciseLibrary,
    exerciseCategories,
    isLoading,
    error
  } = useExercisePlanStore()

  const { toast } = useToast()

  // Filter plans based on search and filters
  const filteredPlans = plans.filter(plan => {
    if (plan.isArchived) return false
    
    const matchesSearch = !searchTerm || 
      plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesDifficulty = selectedFilters.difficulty === 'all' || 
      plan.difficulty === selectedFilters.difficulty
    
    const matchesAssigned = selectedFilters.assignedStatus === 'all' ||
      (selectedFilters.assignedStatus === 'assigned' && plan.assignedClients.length > 0) ||
      (selectedFilters.assignedStatus === 'unassigned' && plan.assignedClients.length === 0)
    
    return matchesSearch && matchesDifficulty && matchesAssigned
  })

  const handleCreatePlan = (planData) => {
    const newPlan = createPlan(planData)
    setShowCreateModal(false)
    toast({
      title: "Plan Created",
      description: `"${newPlan.name}" has been created successfully.`,
      variant: "success",
    })
  }

  const handleSavePlan = () => {
    if (selectedPlan) {
      updatePlan(selectedPlan.id, { updatedAt: new Date().toISOString() })
      toast({
        title: "Plan Saved",
        description: "Your changes have been saved successfully.",
        variant: "success",
      })
    }
  }

  const handleDuplicatePlan = (planId) => {
    const duplicatedPlan = duplicatePlan(planId)
    if (duplicatedPlan) {
      toast({
        title: "Plan Duplicated",
        description: `"${duplicatedPlan.name}" has been created.`,
        variant: "success",
      })
    }
  }

  const handleDeletePlan = (planId) => {
    const planToDelete = plans.find(p => p.id === planId)
    deletePlan(planId)
    toast({
      title: "Plan Deleted",
      description: `"${planToDelete?.name}" has been deleted.`,
      variant: "destructive",
    })
  }

  const handleArchivePlan = (planId) => {
    const planToArchive = plans.find(p => p.id === planId)
    archivePlan(planId)
    toast({
      title: "Plan Archived",
      description: `"${planToArchive?.name}" has been archived.`,
    })
  }

  const loadAPIData = useCallback(async (showSuccessToast = true) => {
    if (!resolvedSessionId || !resolvedClientId) {
      toast({
        title: "Missing context",
        description: "Open the Exercise Plan Builder from a client session so we know which plans to load.",
        variant: "destructive",
      })
      return
    }

    try {
      const exercises = await loadExerciseLibrary()
      const apiData = await fetchExercisePlans({
        sessionId: resolvedSessionId,
        clientId: resolvedClientId
      })
      await loadPlansFromAPI(apiData)
      
      if (showSuccessToast) {
        toast({
          title: "Data Loaded",
          description: `Exercise plans and ${exercises.length} exercises loaded successfully.`,
          variant: "success",
        })
      }
    } catch (error) {
      toast({
        title: "Error Loading Data",
        description: error.message,
        variant: "destructive",
      })
    }
  }, [loadExerciseLibrary, loadPlansFromAPI, resolvedSessionId, resolvedClientId, toast])

  useEffect(() => {
    loadAPIData(false)
  }, [loadAPIData])

  const handleRefreshPlans = () => {
    loadAPIData(true)
  }

  // Auto-select first plan if none selected
  useEffect(() => {
    if (!selectedPlan && filteredPlans.length > 0) {
      setSelectedPlan(filteredPlans[0])
    }
  }, [filteredPlans, selectedPlan, setSelectedPlan])

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header/Toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Exercise Plan Builder</h1>
            <p className="text-slate-600 mt-1">Create and manage workout plans for your clients</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleRefreshPlans}
              disabled={isLoading}
              className="hidden sm:flex"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowExerciseLibrary(true)}
              className="hidden sm:flex"
            >
              <Dumbbell className="h-4 w-4 mr-2" />
              Exercise Library
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowAssignModal(true)}
              disabled={!selectedPlan}
              className="hidden sm:flex"
            >
              <Users className="h-4 w-4 mr-2" />
              Assign to Client
            </Button>
            
            <Button
              variant="outline"
              onClick={handleSavePlan}
              disabled={!selectedPlan}
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            
            <Button
              variant="fitness"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Plan
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Plan Library */}
        <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
          {/* Search and Filters */}
          <div className="p-4 border-b border-slate-200">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search plans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={selectedFilters.difficulty}
                onChange={(e) => setSelectedFilters(prev => ({ ...prev, difficulty: e.target.value }))}
                className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
              
              <select
                value={selectedFilters.assignedStatus}
                onChange={(e) => setSelectedFilters(prev => ({ ...prev, assignedStatus: e.target.value }))}
                className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Plans</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </div>
          </div>

          {/* Plan List */}
          <PlanLibrary
            plans={filteredPlans}
            selectedPlan={selectedPlan}
            onSelectPlan={setSelectedPlan}
            onDuplicatePlan={handleDuplicatePlan}
            onDeletePlan={handleDeletePlan}
            onArchivePlan={handleArchivePlan}
          />
        </div>

        {/* Main Panel - Plan Builder */}
        <div className="flex-1 flex flex-col">
          {selectedPlan ? (
            <PlanBuilder
              plan={selectedPlan}
              onOpenExerciseLibrary={() => setShowExerciseLibrary(true)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Dumbbell className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No Plan Selected</h3>
                <p className="text-slate-600 mb-6">Select a plan from the library or create a new one to get started</p>
                <Button variant="fitness" onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Plan
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreatePlanModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreatePlan={handleCreatePlan}
      />

      <ExerciseLibraryModal
        isOpen={showExerciseLibrary}
        onClose={() => setShowExerciseLibrary(false)}
        selectedPlan={selectedPlan}
      />

      <AssignClientModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        plan={selectedPlan}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading...</p>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
          <p>{error}</p>
        </div>
      )}
    </div>
  )
}

export default ExercisePlanBuilder